import apiService from './apiService';

// baseURL is http://13.232.134.145:5050/api, so paths are relative to /api
const ADMIN_BASE = '/admin';

/**
 * Normalize programs list from API.
 * Supports: { statusCode, message, success, data: [], pagination: { page, limit, totalItems, totalPages, hasNextPage, hasPrevPage } } or legacy shapes.
 */
function normalizeProgramsResponse(res) {
  if (Array.isArray(res)) return { list: res, total: res.length, page: 1, limit: res.length, hasNextPage: false, hasPrevPage: false };
  const pag = res?.pagination;
  const totalItems = pag?.totalItems ?? res.total;
  const pageNum = pag?.page ?? res.page ?? 1;
  const limitNum = pag?.limit ?? res.limit ?? 10;
  const totalPages = pag?.totalPages ?? (totalItems != null && limitNum > 0 ? Math.ceil(totalItems / limitNum) : 1);
  const hasNextPage = pag?.hasNextPage ?? (pageNum < totalPages);
  const hasPrevPage = pag?.hasPrevPage ?? (pageNum > 1);

  if (res?.data && Array.isArray(res.data)) {
    return {
      list: res.data,
      total: totalItems ?? res.data.length,
      page: pageNum,
      limit: limitNum,
      hasNextPage,
      hasPrevPage,
    };
  }
  if (res?.programs && Array.isArray(res.programs)) {
    return {
      list: res.programs,
      total: totalItems ?? res.programs.length,
      page: pageNum,
      limit: limitNum,
      hasNextPage,
      hasPrevPage,
    };
  }
  return { list: [], total: 0, page: 1, limit: 10, hasNextPage: false, hasPrevPage: false };
}

export const authApi = {
  login: (email, password) =>
    apiService.post(`${ADMIN_BASE}/auth/login`, { email, password }),
};

/**
 * Public programs API: GET /programs?page=1&limit=10 (no auth).
 * Use on public Programs page.
 */
export const programsPublicApi = {
  list: (page = 1, limit = 10) =>
    apiService
      .get('/programs', { params: { page, limit } })
      .then(normalizeProgramsResponse),
};

/**
 * Admin programs API: GET /admin/programs (auth required).
 * @param {number} [page=1]
 * @param {number} [limit=10]
 */
export const programsApi = {
  list: (page = 1, limit = 10) =>
    apiService
      .get(`${ADMIN_BASE}/programs`, { params: { page, limit } })
      .then(normalizeProgramsResponse),

  getOne: (id) =>
    apiService
      .get(`${ADMIN_BASE}/programs/${id}`)
      .then((res) => res?.data ?? res),

  /**
   * Create program (multipart/form-data).
   * @param {{
   *   name: string;
   *   description: string;
   *   age_min: number;
   *   age_max?: number;
   *   feature_timeline: string;
   *   feature_ratio: string;
   *   icon?: File | null;
   *   photo?: File | null;
   * }} fields
   */
  create: (fields) => {
    const form = new FormData();
    form.append('name', fields.name);
    form.append('description', fields.description);
    form.append('age_min', String(Number(fields.age_min) || 0));
    if (fields.age_max != null && fields.age_max !== '') {
      form.append('age_max', String(Number(fields.age_max) || 0));
    }
    form.append('feature_timeline', fields.feature_timeline || '');
    form.append('feature_ratio', fields.feature_ratio || '');
    if (fields.icon instanceof File) form.append('icon', fields.icon);
    if (fields.photo instanceof File) form.append('photo', fields.photo);
    return apiService
      .post(`${ADMIN_BASE}/programs`, form)
      .then((res) => res?.data ?? res);
  },

  /**
   * Update program (PATCH). Uses FormData when icon/photo are present (multipart), else JSON.
   * @param {string} id - Program ID
   * @param {object} fields - Same shape as create (name, description, age_min, age_max, feature_timeline, feature_ratio, icon?, photo?)
   */
  update: (id, fields) => {
    const hasFiles = fields.icon instanceof File || fields.photo instanceof File;
    if (hasFiles) {
      const form = new FormData();
      form.append('name', fields.name ?? '');
      form.append('description', fields.description ?? '');
      form.append('age_min', String(Number(fields.age_min) || 0));
      if (fields.age_max != null && fields.age_max !== '') {
        form.append('age_max', String(Number(fields.age_max) || 0));
      }
      form.append('feature_timeline', fields.feature_timeline ?? '');
      form.append('feature_ratio', fields.feature_ratio ?? '');
      if (fields.icon instanceof File) form.append('icon', fields.icon);
      if (fields.photo instanceof File) form.append('photo', fields.photo);
      return apiService.patch(`${ADMIN_BASE}/programs/${id}`, form);
    }
    return apiService.patch(`${ADMIN_BASE}/programs/${id}`, {
      name: fields.name,
      description: fields.description,
      age_min: fields.age_min,
      age_max: fields.age_max,
      feature_timeline: fields.feature_timeline,
      feature_ratio: fields.feature_ratio,
    });
  },

  delete: (id) =>
    apiService.delete(`${ADMIN_BASE}/programs/${id}`),
};

/**
 * Normalize inquiries list from API.
 */
function normalizeInquiriesResponse(res) {
  if (Array.isArray(res)) return { list: res, total: res.length, page: 1, limit: res.length };
  const pag = res?.pagination;
  if (res?.data && Array.isArray(res.data)) {
    return {
      list: res.data,
      total: pag?.totalItems ?? res.total ?? res.data.length,
      page: pag?.page ?? res.page ?? 1,
      limit: pag?.limit ?? res.limit ?? 10,
    };
  }
  if (res?.inquiries && Array.isArray(res.inquiries)) {
    return {
      list: res.inquiries,
      total: pag?.totalItems ?? res.total ?? res.inquiries.length,
      page: pag?.page ?? res.page ?? 1,
      limit: pag?.limit ?? res.limit ?? 10,
    };
  }
  return { list: [], total: 0, page: 1, limit: 10 };
}

/**
 * Public inquiries API (no auth). Used on Contact page.
 * POST /inquiries
 * Payload: { parent_name, child_name, child_age?, phone?, email, message? }
 * @param {{ parent_name: string; child_name: string; child_age?: number; phone?: string; email: string; message?: string }} data
 */
export const inquiriesPublicApi = {
  create: (data) => {
    const payload = {
      parent_name: data.parent_name ?? '',
      child_name: data.child_name ?? '',
      email: data.email ?? '',
    };
    const ageNum = data.child_age != null && data.child_age !== '' ? parseInt(data.child_age, 10) : undefined;
    if (ageNum != null && !Number.isNaN(ageNum)) payload.child_age = ageNum;
    if (data.phone) payload.phone = data.phone;
    if (data.message) payload.message = data.message;
    return apiService.post('/inquiries', payload);
  },
};

export const inquiriesApi = {
  list: (page = 1, limit = 10) =>
    apiService
      .get(`${ADMIN_BASE}/inquiries`, { params: { page, limit } })
      .then(normalizeInquiriesResponse),

  getOne: (id) =>
    apiService
      .get(`${ADMIN_BASE}/inquiries/${id}`)
      .then((res) => res?.data ?? res),
};

/**
 * Normalize bookings list from API.
 */
function normalizeBookingsResponse(res) {
  if (Array.isArray(res)) return { list: res, total: res.length, page: 1, limit: res.length };
  const pag = res?.pagination;
  if (res?.data && Array.isArray(res.data)) {
    return {
      list: res.data,
      total: pag?.totalItems ?? res.total ?? res.data.length,
      page: pag?.page ?? res.page ?? 1,
      limit: pag?.limit ?? res.limit ?? 10,
    };
  }
  if (res?.bookings && Array.isArray(res.bookings)) {
    return {
      list: res.bookings,
      total: pag?.totalItems ?? res.total ?? res.bookings.length,
      page: pag?.page ?? res.page ?? 1,
      limit: pag?.limit ?? res.limit ?? 10,
    };
  }
  if (res?.slot_bookings && Array.isArray(res.slot_bookings)) {
    return {
      list: res.slot_bookings,
      total: pag?.totalItems ?? res.total ?? res.slot_bookings.length,
      page: pag?.page ?? res.page ?? 1,
      limit: pag?.limit ?? res.limit ?? 10,
    };
  }
  return { list: [], total: 0, page: 1, limit: 10 };
}

export const bookingsApi = {
  list: (page = 1, limit = 10, startDate = null, endDate = null, status = null) => {
    const params = { page, limit };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (status && status !== 'all') params.status = status;
    return apiService
      .get(`${ADMIN_BASE}/slot-bookings`, { params })
      .then(normalizeBookingsResponse);
  },

  getOne: (id) =>
    apiService
      .get(`${ADMIN_BASE}/slot-bookings/${id}`)
      .then((res) => res?.data ?? res),
};

/**
 * Public slot bookings API (no auth). Used on Admissions page.
 * GET /slot-bookings/check-availability?date=ISO_DATE
 * @param {Date} date - Selected date
 * @returns {Promise<{ date: string; booked_slots: Array<{ slot_start: string; slot_end: string }> }>}
 */
export const slotBookingsPublicApi = {
  checkAvailability: (date) =>
    apiService
      .get('/slot-bookings/check-availability', {
        params: { date: date.toISOString() },
      })
      .then((res) => (res?.success ? res?.data : { date: null, booked_slots: [] })),

  /**
   * Create a slot booking (tour inquiry).
   * POST /slot-bookings
   * @param {{
   *   slot_start: string;
   *   slot_end: string;
   *   parent_name: string;
   *   child_name: string;
   *   child_age: number;
   *   phone_number: string;
   *   email: string;
   *   message?: string;
   * }} payload
   */
  create: (payload) =>
    apiService.post('/slot-bookings', payload),
};

/**
 * Normalize contacts list from API.
 */
function normalizeContactsResponse(res) {
  if (Array.isArray(res)) return { list: res, total: res.length, page: 1, limit: res.length };
  const pag = res?.pagination;
  if (res?.data && Array.isArray(res.data)) {
    return {
      list: res.data,
      total: pag?.totalItems ?? res.total ?? res.data.length,
      page: pag?.page ?? res.page ?? 1,
      limit: pag?.limit ?? res.limit ?? 10,
    };
  }
  if (res?.contacts && Array.isArray(res.contacts)) {
    return {
      list: res.contacts,
      total: pag?.totalItems ?? res.total ?? res.contacts.length,
      page: pag?.page ?? res.page ?? 1,
      limit: pag?.limit ?? res.limit ?? 10,
    };
  }
  return { list: [], total: 0, page: 1, limit: 10 };
}

/**
 * Public contact info API (no auth). Used on Contact page.
 * GET /contact-info
 * @returns {Promise<{ address: string; phone: string; email: string; hours: string }>}
 */
export const contactInfoPublicApi = {
  get: () =>
    apiService
      .get('/contact-info')
      .then((res) => (res?.success ? res?.data : null)),
};

export const contactsApi = {
  list: (page = 1, limit = 10) =>
    apiService
      .get(`${ADMIN_BASE}/contacts`, { params: { page, limit } })
      .then(normalizeContactsResponse),

  getOne: (id) =>
    apiService
      .get(`${ADMIN_BASE}/contacts/${id}`)
      .then((res) => res?.data ?? res),

  create: (fields) =>
    apiService
      .post(`${ADMIN_BASE}/contacts`, fields)
      .then((res) => res?.data ?? res),

  update: (id, fields) =>
    apiService.patch(`${ADMIN_BASE}/contacts/${id}`, fields),

  delete: (id) =>
    apiService.delete(`${ADMIN_BASE}/contacts/${id}`),
};

/**
 * Normalize testimonials list from API.
 */
function normalizeTestimonialsResponse(res) {
  if (Array.isArray(res)) return { list: res, total: res.length, page: 1, limit: res.length, hasNextPage: false, hasPrevPage: false };
  const pag = res?.pagination;
  const totalItems = pag?.totalItems ?? res.total;
  const pageNum = pag?.page ?? res.page ?? 1;
  const limitNum = pag?.limit ?? res.limit ?? 10;
  const totalPages = pag?.totalPages ?? (totalItems != null && limitNum > 0 ? Math.ceil(totalItems / limitNum) : 1);
  const hasNextPage = pag?.hasNextPage ?? (pageNum < totalPages);
  const hasPrevPage = pag?.hasPrevPage ?? (pageNum > 1);

  if (res?.data && Array.isArray(res.data)) {
    return {
      list: res.data,
      total: totalItems ?? res.data.length,
      page: pageNum,
      limit: limitNum,
      hasNextPage,
      hasPrevPage,
    };
  }
  if (res?.testimonials && Array.isArray(res.testimonials)) {
    return {
      list: res.testimonials,
      total: totalItems ?? res.testimonials.length,
      page: pageNum,
      limit: limitNum,
      hasNextPage,
      hasPrevPage,
    };
  }
  return { list: [], total: 0, page: 1, limit: 10, hasNextPage: false, hasPrevPage: false };
}

/**
 * Public testimonials API (no auth).
 * GET /testimonials?page=1&limit=10
 */
export const testimonialsPublicApi = {
  list: (page = 1, limit = 10) =>
    apiService
      .get('/testimonials', { params: { page, limit } })
      .then(normalizeTestimonialsResponse),
};

/**
 * Admin testimonials API (auth required).
 * GET  /admin/testimonials?page=1&limit=10
 * POST /admin/testimonials  (multipart/form-data)
 */
export const testimonialsApi = {
  list: (page = 1, limit = 10) =>
    apiService
      .get(`${ADMIN_BASE}/testimonials`, { params: { page, limit } })
      .then(normalizeTestimonialsResponse),

  getOne: (id) =>
    apiService
      .get(`${ADMIN_BASE}/testimonials/${id}`)
      .then((res) => res?.data ?? res),

  /**
   * Create a testimonial (multipart/form-data).
   * @param {{
   *   name: string;
   *   review: string;
   *   rating: number;
   *   is_featured?: string;
   *   is_verified?: string;
   *   avatar?: File | null;
   *   photo?: File | null;
   *   video?: File | null;
   * }} fields
   */
  create: (fields) => {
    const form = new FormData();
    form.append('name', fields.name);
    form.append('review', fields.review);
    form.append('rating', String(Number(fields.rating)));
    if (fields.is_featured !== '' && fields.is_featured != null) {
      form.append('is_featured', String(fields.is_featured));
    }
    if (fields.is_verified !== '' && fields.is_verified != null) {
      form.append('is_verified', String(fields.is_verified));
    }
    if (fields.avatar instanceof File) form.append('avatar', fields.avatar);
    if (fields.photo instanceof File) form.append('photo', fields.photo);
    if (fields.video instanceof File) form.append('video', fields.video);
    return apiService
      .post(`${ADMIN_BASE}/testimonials`, form)
      .then((res) => res?.data ?? res);
  },

  /**
   * Update a testimonial (PATCH, multipart/form-data).
   * Only appends file fields when a new File is provided; text fields are always sent.
   */
  update: (id, fields) => {
    const form = new FormData();
    form.append('name', fields.name);
    form.append('review', fields.review);
    form.append('rating', String(Number(fields.rating)));
    if (fields.is_featured !== '' && fields.is_featured != null) {
      form.append('is_featured', String(fields.is_featured));
    }
    if (fields.is_verified !== '' && fields.is_verified != null) {
      form.append('is_verified', String(fields.is_verified));
    }
    if (fields.avatar instanceof File) form.append('avatar', fields.avatar);
    if (fields.photo instanceof File) form.append('photo', fields.photo);
    if (fields.video instanceof File) form.append('video', fields.video);
    return apiService
      .patch(`${ADMIN_BASE}/testimonials/${id}`, form)
      .then((res) => res?.data ?? res);
  },

  delete: (id) =>
    apiService.delete(`${ADMIN_BASE}/testimonials/${id}`),
};

/**
 * Normalize galleries list from API.
 */
function normalizeGalleriesResponse(res) {
  if (Array.isArray(res)) {
    return { list: res, total: res.length, page: 1, limit: res.length, hasNextPage: false, hasPrevPage: false };
  }
  const pag = res?.pagination;
  const totalItems = pag?.totalItems ?? res.total;
  const pageNum = pag?.page ?? res.page ?? 1;
  const limitNum = pag?.limit ?? res.limit ?? 10;
  const totalPages = pag?.totalPages ?? (totalItems != null && limitNum > 0 ? Math.ceil(totalItems / limitNum) : 1);
  const hasNextPage = pag?.hasNextPage ?? pageNum < totalPages;
  const hasPrevPage = pag?.hasPrevPage ?? pageNum > 1;

  if (res?.data && Array.isArray(res.data)) {
    return {
      list: res.data,
      total: totalItems ?? res.data.length,
      page: pageNum,
      limit: limitNum,
      hasNextPage,
      hasPrevPage,
    };
  }
  if (res?.galleries && Array.isArray(res.galleries)) {
    return {
      list: res.galleries,
      total: totalItems ?? res.galleries.length,
      page: pageNum,
      limit: limitNum,
      hasNextPage,
      hasPrevPage,
    };
  }
  return { list: [], total: 0, page: 1, limit: 10, hasNextPage: false, hasPrevPage: false };
}

/**
 * Single-gallery API envelope: { statusCode, message, data: { id, title, images, ... }, success }
 * or the entity returned directly.
 */
function unwrapGalleryPayload(res) {
  if (res == null || typeof res !== 'object') return res;
  const inner = res.data;
  if (
    inner != null &&
    typeof inner === 'object' &&
    !Array.isArray(inner) &&
    (inner.id != null || inner._id != null)
  ) {
    return inner;
  }
  if (
    inner != null &&
    typeof inner === 'object' &&
    !Array.isArray(inner) &&
    inner.gallery != null &&
    typeof inner.gallery === 'object' &&
    (inner.gallery.id != null || inner.gallery._id != null)
  ) {
    return inner.gallery;
  }
  if (res.gallery != null && typeof res.gallery === 'object' && (res.gallery.id != null || res.gallery._id != null)) {
    return res.gallery;
  }
  return res;
}

/**
 * Admin galleries API (auth required). Base: /api/admin/galleries
 * POST   /admin/galleries — create
 * GET    /admin/galleries — list (paginated)
 * DELETE /admin/galleries — soft-delete all galleries (+ S3 objects)
 * GET    /admin/galleries/:id — one gallery
 * PATCH  /admin/galleries/:id — update title and/or append images (multipart)
 * DELETE /admin/galleries/:id — soft-delete one gallery (+ S3)
 * DELETE /admin/galleries/:id/images/:imageId — remove one image
 */
export const galleriesApi = {
  list: (page = 1, limit = 100) =>
    apiService
      .get(`${ADMIN_BASE}/galleries`, { params: { page, limit } })
      .then(normalizeGalleriesResponse),

  getOne: (id) =>
    apiService.get(`${ADMIN_BASE}/galleries/${id}`).then(unwrapGalleryPayload),

  /**
   * @param {{ title: string; images?: File[] }} fields
   */
  create: (fields) => {
    const form = new FormData();
    form.append('title', fields.title ?? '');
    const files = Array.isArray(fields.images) ? fields.images : [];
    files.forEach((file) => {
      if (file instanceof File) form.append('images', file);
    });
    return apiService
      .post(`${ADMIN_BASE}/galleries`, form)
      .then(unwrapGalleryPayload);
  },

  /**
   * Update title and/or append images (multipart).
   * @param {string} id
   * @param {{ title?: string; images?: File[] }} fields
   */
  update: (id, fields) => {
    const form = new FormData();
    if (fields.title != null) form.append('title', fields.title);
    const files = Array.isArray(fields.images) ? fields.images : [];
    files.forEach((file) => {
      if (file instanceof File) form.append('images', file);
    });
    return apiService
      .patch(`${ADMIN_BASE}/galleries/${id}`, form)
      .then(unwrapGalleryPayload);
  },

  /** Soft-delete one gallery (removes S3 objects for its images). */
  delete: (id) => apiService.delete(`${ADMIN_BASE}/galleries/${id}`),

  /** Soft-delete all galleries (removes S3 objects for all images). */
  deleteAll: () => apiService.delete(`${ADMIN_BASE}/galleries`),

  /** Remove a single image from a gallery. */
  deleteImage: (galleryId, imageId) =>
    apiService.delete(`${ADMIN_BASE}/galleries/${galleryId}/images/${imageId}`),
};

/**
 * Public galleries API (no auth).
 * GET /galleries?page=&limit=
 * GET /galleries/:id
 */
export const galleriesPublicApi = {
  list: (page = 1, limit = 50) =>
    apiService
      .get('/galleries', { params: { page, limit } })
      .then(normalizeGalleriesResponse),

  getOne: (id) =>
    apiService
      .get(`/galleries/${encodeURIComponent(id)}`)
      .then(unwrapGalleryPayload),
};

/**
 * Normalize announcements list from API (paginated envelope).
 */
function normalizeAnnouncementsResponse(res) {
  if (Array.isArray(res)) {
    return { list: res, total: res.length, page: 1, limit: res.length, hasNextPage: false, hasPrevPage: false };
  }
  const pag = res?.pagination;
  const totalItems = pag?.totalItems ?? res.total;
  const pageNum = pag?.page ?? res.page ?? 1;
  const limitNum = pag?.limit ?? res.limit ?? 10;
  const totalPages = pag?.totalPages ?? (totalItems != null && limitNum > 0 ? Math.ceil(totalItems / limitNum) : 1);
  const hasNextPage = pag?.hasNextPage ?? pageNum < totalPages;
  const hasPrevPage = pag?.hasPrevPage ?? pageNum > 1;

  if (res?.data && Array.isArray(res.data)) {
    return {
      list: res.data,
      total: totalItems ?? res.data.length,
      page: pageNum,
      limit: limitNum,
      hasNextPage,
      hasPrevPage,
    };
  }
  if (res?.announcements && Array.isArray(res.announcements)) {
    return {
      list: res.announcements,
      total: totalItems ?? res.announcements.length,
      page: pageNum,
      limit: limitNum,
      hasNextPage,
      hasPrevPage,
    };
  }
  return { list: [], total: 0, page: 1, limit: 10, hasNextPage: false, hasPrevPage: false };
}

/**
 * Single-announcement API envelope (GET/PATCH/POST one):
 * { statusCode, message, data: { id, title, description, announcement_date, creator, ... }, success }
 */
function unwrapAnnouncementPayload(res) {
  if (res == null || typeof res !== 'object') return res;
  const inner = res.data;
  if (
    inner != null &&
    typeof inner === 'object' &&
    !Array.isArray(inner) &&
    (inner.id != null || inner._id != null)
  ) {
    return inner;
  }
  return res;
}

/**
 * Admin announcements API (auth). Base: /api/admin/announcements
 * POST/PATCH body: { title, description, announcement_date } (date as YYYY-MM-DD)
 */
function announcementWriteBody(fields) {
  return {
    title: fields.title ?? '',
    description: fields.description ?? '',
    announcement_date: fields.announcement_date ?? '',
  };
}

export const announcementsApi = {
  list: (page = 1, limit = 100) =>
    apiService
      .get(`${ADMIN_BASE}/announcements`, { params: { page, limit } })
      .then(normalizeAnnouncementsResponse),

  getOne: (id) =>
    apiService.get(`${ADMIN_BASE}/announcements/${id}`).then(unwrapAnnouncementPayload),

  create: (fields) =>
    apiService
      .post(`${ADMIN_BASE}/announcements`, announcementWriteBody(fields))
      .then(unwrapAnnouncementPayload),

  update: (id, fields) =>
    apiService
      .patch(`${ADMIN_BASE}/announcements/${id}`, announcementWriteBody(fields))
      .then(unwrapAnnouncementPayload),

  delete: (id) => apiService.delete(`${ADMIN_BASE}/announcements/${id}`),

  deleteAll: () => apiService.delete(`${ADMIN_BASE}/announcements`),
};

/**
 * Public announcements API (no auth).
 * GET /announcements?page=&limit=
 * GET /announcements/:id
 */
export const announcementsPublicApi = {
  list: (page = 1, limit = 50) =>
    apiService
      .get('/announcements', { params: { page, limit } })
      .then(normalizeAnnouncementsResponse),

  getOne: (id) =>
    apiService
      .get(`/announcements/${encodeURIComponent(id)}`)
      .then(unwrapAnnouncementPayload),
};

/**
 * Normalize events list from API.
 * Envelope: { data: [...], pagination: { page, limit, totalItems, totalPages, hasNextPage, hasPrevPage } }
 * Row: { id, title, description, event_date (often ISO e.g. 2026-04-11T00:00:00.000Z), creator, created_at, ... }
 */
function normalizeEventsResponse(res) {
  if (Array.isArray(res)) {
    return { list: res, total: res.length, page: 1, limit: res.length, hasNextPage: false, hasPrevPage: false };
  }
  const pag = res?.pagination;
  const totalItems = pag?.totalItems ?? res.total;
  const pageNum = pag?.page ?? res.page ?? 1;
  const limitNum = pag?.limit ?? res.limit ?? 10;
  const totalPages = pag?.totalPages ?? (totalItems != null && limitNum > 0 ? Math.ceil(totalItems / limitNum) : 1);
  const hasNextPage = pag?.hasNextPage ?? pageNum < totalPages;
  const hasPrevPage = pag?.hasPrevPage ?? pageNum > 1;

  if (res?.data && Array.isArray(res.data)) {
    return {
      list: res.data,
      total: totalItems ?? res.data.length,
      page: pageNum,
      limit: limitNum,
      hasNextPage,
      hasPrevPage,
    };
  }
  if (res?.events && Array.isArray(res.events)) {
    return {
      list: res.events,
      total: totalItems ?? res.events.length,
      page: pageNum,
      limit: limitNum,
      hasNextPage,
      hasPrevPage,
    };
  }
  return { list: [], total: 0, page: 1, limit: 10, hasNextPage: false, hasPrevPage: false };
}

/**
 * Single-event envelope: { data: { id, title, ... } }
 */
function unwrapEventPayload(res) {
  if (res == null || typeof res !== 'object') return res;
  const inner = res.data;
  if (
    inner != null &&
    typeof inner === 'object' &&
    !Array.isArray(inner) &&
    (inner.id != null || inner._id != null)
  ) {
    return inner;
  }
  return res;
}

/**
 * Admin events API (auth). Base: /api/admin/events
 * POST/PATCH JSON body:
 * { "title": string, "description": string, "event_date": "YYYY-MM-DD" }
 */
function eventWriteBody(fields) {
  return {
    title: fields.title ?? '',
    description: fields.description ?? '',
    event_date: fields.event_date ?? '',
  };
}

export const eventsApi = {
  list: (page = 1, limit = 100) =>
    apiService
      .get(`${ADMIN_BASE}/events`, { params: { page, limit } })
      .then(normalizeEventsResponse),

  getOne: (id) => apiService.get(`${ADMIN_BASE}/events/${id}`).then(unwrapEventPayload),

  create: (fields) =>
    apiService.post(`${ADMIN_BASE}/events`, eventWriteBody(fields)).then(unwrapEventPayload),

  update: (id, fields) =>
    apiService.patch(`${ADMIN_BASE}/events/${id}`, eventWriteBody(fields)).then(unwrapEventPayload),

  delete: (id) => apiService.delete(`${ADMIN_BASE}/events/${id}`),

  deleteAll: () => apiService.delete(`${ADMIN_BASE}/events`),
};

/**
 * Public events API (no auth). Server returns only upcoming events (from start of today UTC).
 * GET /events?page=&limit=
 * GET /events/:id — 404 if event is before today (UTC)
 */
export const eventsPublicApi = {
  list: (page = 1, limit = 50) =>
    apiService
      .get('/events', { params: { page, limit } })
      .then(normalizeEventsResponse),

  getOne: (id) =>
    apiService
      .get(`/events/${encodeURIComponent(id)}`)
      .then(unwrapEventPayload),
};

/**
 * Normalize scheduled closings list.
 * Envelope: { statusCode, message, data: [...], pagination: { page, limit, totalItems, totalPages, hasNextPage, hasPrevPage }, success }
 * Row: { id, title, closing_date (ISO e.g. 2026-05-08T00:00:00.000Z), created_by, deleted_at, created_at, updated_at, creator?: { id, name, email } }
 */
function normalizeScheduledClosingsResponse(res) {
  if (Array.isArray(res)) {
    return { list: res, total: res.length, page: 1, limit: res.length, hasNextPage: false, hasPrevPage: false };
  }
  const pag = res?.pagination;
  const totalItems = pag?.totalItems ?? res.total;
  const pageNum = pag?.page ?? res.page ?? 1;
  const limitNum = pag?.limit ?? res.limit ?? 10;
  const totalPages = pag?.totalPages ?? (totalItems != null && limitNum > 0 ? Math.ceil(totalItems / limitNum) : 1);
  const hasNextPage = pag?.hasNextPage ?? pageNum < totalPages;
  const hasPrevPage = pag?.hasPrevPage ?? pageNum > 1;

  if (res?.data && Array.isArray(res.data)) {
    return {
      list: res.data,
      total: totalItems ?? res.data.length,
      page: pageNum,
      limit: limitNum,
      hasNextPage,
      hasPrevPage,
    };
  }
  if (res?.scheduled_closings && Array.isArray(res.scheduled_closings)) {
    return {
      list: res.scheduled_closings,
      total: totalItems ?? res.scheduled_closings.length,
      page: pageNum,
      limit: limitNum,
      hasNextPage,
      hasPrevPage,
    };
  }
  if (res?.scheduledClosings && Array.isArray(res.scheduledClosings)) {
    return {
      list: res.scheduledClosings,
      total: totalItems ?? res.scheduledClosings.length,
      page: pageNum,
      limit: limitNum,
      hasNextPage,
      hasPrevPage,
    };
  }
  return { list: [], total: 0, page: 1, limit: 10, hasNextPage: false, hasPrevPage: false };
}

/** Single row envelope: { data: { id, title, closing_date, creator?, ... } } or row at root. */
function unwrapScheduledClosingPayload(res) {
  if (res == null || typeof res !== 'object') return res;
  const inner = res.data;
  if (
    inner != null &&
    typeof inner === 'object' &&
    !Array.isArray(inner) &&
    (inner.id != null || inner._id != null)
  ) {
    return inner;
  }
  return res;
}

/**
 * Admin scheduled closings API. Base: /api/admin/scheduled-closings
 * POST/PATCH JSON: { title, closing_date: "YYYY-MM-DD" }
 */
function scheduledClosingWriteBody(fields) {
  return {
    title: fields.title ?? '',
    closing_date: fields.closing_date ?? '',
  };
}

export const scheduledClosingsApi = {
  list: (page = 1, limit = 100) =>
    apiService
      .get(`${ADMIN_BASE}/scheduled-closings`, { params: { page, limit } })
      .then(normalizeScheduledClosingsResponse),

  getOne: (id) =>
    apiService
      .get(`${ADMIN_BASE}/scheduled-closings/${encodeURIComponent(id)}`)
      .then(unwrapScheduledClosingPayload),

  create: (fields) =>
    apiService
      .post(`${ADMIN_BASE}/scheduled-closings`, scheduledClosingWriteBody(fields))
      .then(unwrapScheduledClosingPayload),

  update: (id, fields) =>
    apiService
      .patch(`${ADMIN_BASE}/scheduled-closings/${encodeURIComponent(id)}`, scheduledClosingWriteBody(fields))
      .then(unwrapScheduledClosingPayload),

  delete: (id) => apiService.delete(`${ADMIN_BASE}/scheduled-closings/${encodeURIComponent(id)}`),

  deleteAll: () => apiService.delete(`${ADMIN_BASE}/scheduled-closings`),
};

/**
 * Public scheduled closings (no auth). Sorted by closing_date ascending on server.
 * GET /scheduled-closings?page=&limit=
 * GET /scheduled-closings/:id
 */
export const scheduledClosingsPublicApi = {
  list: (page = 1, limit = 100) =>
    apiService
      .get('/scheduled-closings', { params: { page, limit } })
      .then(normalizeScheduledClosingsResponse),

  getOne: (id) =>
    apiService
      .get(`/scheduled-closings/${encodeURIComponent(id)}`)
      .then(unwrapScheduledClosingPayload),
};
